import React, { useState, useEffect } from 'react';
import { reviewsAPI, usersAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Skeleton } from '../../components/ui/skeleton';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Award, Plus, Star } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminReviews() {
  const [reviews, setReviews] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ employee_id: '', rating: '3', strengths: '', areas_for_improvement: '', notes: '', goals: '' });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [revRes, staffRes] = await Promise.all([
        reviewsAPI.list({ limit: 50 }),
        usersAPI.list({ limit: 100 }).catch(() => ({ data: { users: [] } }))
      ]);
      setReviews(revRes.data.reviews || []);
      setStaff(staffRes.data.users || []);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  const createReview = async () => {
    if (!form.employee_id) { toast.error('Select employee'); return; }
    try {
      await reviewsAPI.create({
        ...form,
        rating: parseInt(form.rating),
        goals: form.goals ? form.goals.split('\n').filter(g => g.trim()) : []
      });
      toast.success('Review created');
      setShowCreate(false);
      setForm({ employee_id: '', rating: '3', strengths: '', areas_for_improvement: '', notes: '', goals: '' });
      loadData();
    } catch { toast.error('Failed'); }
  };

  const RATING_LABELS = { 1: 'Needs Improvement', 2: 'Below Expectations', 3: 'Meets Expectations', 4: 'Exceeds Expectations', 5: 'Outstanding' };

  return (
    <div className="space-y-6" data-testid="admin-reviews-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Award className="w-6 h-6 text-[#B89B5E]" /> Performance Reviews</h1>
          <p className="text-sm text-[#5B616E] mt-1">Track team performance and development goals</p>
        </div>
        <Button onClick={() => setShowCreate(true)} data-testid="create-review-btn" className="bg-[#121316] text-white hover:bg-[#1B1D22]"><Plus className="w-4 h-4 mr-2" /> New Review</Button>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{[1,2,3].map(i => <Skeleton key={i} className="h-48 rounded-xl" />)}</div>
      ) : reviews.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {reviews.map(r => (
            <Card key={r.id} className="bg-white border-[#E7E0D6]" data-testid="review-card">
              <CardContent className="pt-5">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-medium">{r.employee_name || 'Staff'}</p>
                    <p className="text-xs text-[#5B616E]">{r.cycle} &middot; Reviewed by {r.reviewer_name || 'Manager'}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    {Array.from({length: 5}).map((_, i) => (
                      <Star key={i} className={`w-4 h-4 ${i < (r.rating || 0) ? 'text-[#B89B5E] fill-[#B89B5E]' : 'text-[#E7E0D6]'}`} />
                    ))}
                  </div>
                </div>
                <Badge variant="outline" className="text-[10px] mt-2">{RATING_LABELS[r.rating] || 'Unrated'}</Badge>
                {r.strengths && <div className="mt-3"><p className="text-[10px] font-medium text-[#5B616E] uppercase">Strengths</p><p className="text-xs mt-0.5">{r.strengths}</p></div>}
                {r.areas_for_improvement && <div className="mt-2"><p className="text-[10px] font-medium text-[#5B616E] uppercase">Areas for Improvement</p><p className="text-xs mt-0.5">{r.areas_for_improvement}</p></div>}
                {r.goals?.length > 0 && <div className="mt-2"><p className="text-[10px] font-medium text-[#5B616E] uppercase">Goals</p>{r.goals.map((g, i) => <p key={i} className="text-xs mt-0.5">&bull; {g}</p>)}</div>}
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-center py-16 text-[#5B616E]"><Award className="w-16 h-16 mx-auto mb-4 opacity-30" /><p>No reviews yet</p></div>
      )}

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle>New Performance Review</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><label className="text-sm font-medium mb-1 block">Employee</label>
              <Select value={form.employee_id} onValueChange={v => setForm({ ...form, employee_id: v })}>
                <SelectTrigger><SelectValue placeholder="Select employee" /></SelectTrigger>
                <SelectContent>{staff.map(s => <SelectItem key={s.id} value={s.id}>{s.full_name} ({s.role})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><label className="text-sm font-medium mb-1 block">Rating (1-5)</label>
              <Select value={form.rating} onValueChange={v => setForm({ ...form, rating: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{[1,2,3,4,5].map(r => <SelectItem key={r} value={String(r)}>{r} - {RATING_LABELS[r]}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><label className="text-sm font-medium mb-1 block">Strengths</label><Textarea value={form.strengths} onChange={e => setForm({ ...form, strengths: e.target.value })} /></div>
            <div><label className="text-sm font-medium mb-1 block">Areas for Improvement</label><Textarea value={form.areas_for_improvement} onChange={e => setForm({ ...form, areas_for_improvement: e.target.value })} /></div>
            <div><label className="text-sm font-medium mb-1 block">Goals (one per line)</label><Textarea value={form.goals} onChange={e => setForm({ ...form, goals: e.target.value })} placeholder="Complete training...\nIncrease output..." /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={createReview} className="bg-[#121316] text-white hover:bg-[#1B1D22]">Create Review</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
